/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

import { Module, Node } from '@msdyn365-commerce-modules/utilities';

import {
    IAccountPreference,
    IAccountPreferences,
    IAccountProfileItem,
    IAccountProfileViewProps
} from '@msdyn365-commerce-modules/account-management';
import { IAccountProfileProps } from '../definition-extensions/account-profile.ext.props.autogenerated';

const AccountPreferencesSection: React.FC<IAccountPreferences> = ({ accountPreferences, heading, personalization, extraPreferences }) => {
    return (
        <Node {...accountPreferences}>
            {heading}
            <AccountPreferenceSection {...personalization} />
            {extraPreferences &&
                extraPreferences.map((preferenceSecton, index) => {
                    return <AccountPreferenceSection {...preferenceSecton} key={index} />;
                })}
        </Node>
    );
};

const AccountPreferenceSection: React.FC<IAccountPreference> = ({
    accountPreference,
    heading,
    description,
    buttonWrapper,
    buttonYesText,
    button,
    buttonNoText
}) => {
    return (
        <Node {...accountPreference}>
            {heading}
            {description}
            <Node {...buttonWrapper}>
                {buttonNoText}
                {button}
                {buttonYesText}
            </Node>
        </Node>
    );
};

const AccountProfileSection: React.FC<IAccountProfileItem> = ({ AccountProfileItem, heading, links, description }) => {
    return (
        <Node {...AccountProfileItem}>
            {heading}
            {description}
            {links}
        </Node>
    );
};

const AccountProfileView: React.FC<IAccountProfileViewProps & IAccountProfileProps<{}>> = props => {
    const {
        AccountProfile,
        accountProfileWrapper,
        heading,
        emailSection,
        nameSection,
        preferenceSection,
        customerAttributesWrapper,
        customerAttributesSection
    } = props;

    React.useEffect(() => {
        if (document.getElementsByClassName('ms-account-profile__attributes-GenericMarketing').length) {
            // @ts-ignore
            // tslint:disable-next-line:no-inner-html
            document.getElementsByClassName('ms-account-profile__attributes-GenericMarketing')[1].innerHTML =
                // @ts-ignore
                props.config.textGenericMarketing;
        }
        if (document.getElementsByClassName('ms-account-profile__attributes-Profiling').length) {
            // @ts-ignore
            // tslint:disable-next-line:no-inner-html
            document.getElementsByClassName('ms-account-profile__attributes-Profiling')[1].innerHTML = props.config.textProfiling;
        }
        if (document.getElementsByClassName('ms-account-profile__attributes-ThirdParty').length) {
            // @ts-ignore
            // tslint:disable-next-line:no-inner-html
            document.getElementsByClassName('ms-account-profile__attributes-ThirdParty')[1].innerHTML = props.config.textThirdParty;
        }

        document.getElementsByClassName('ms-account-profile__attributes-edit-button msc-btn')[0].addEventListener('click', () => {
            const bd_input: HTMLInputElement = document.getElementsByClassName(
                'ms-account-profile__attributes-element ms-account-profile__attributes_input ms-account-profile__attributes_input-edit-false ms-account-profile__Birthday-input'
            )[0] as HTMLInputElement;
            // const prev_date = bd_input.value;
            if (props.context.request.canonicalDomain !== 'us.venchi.com') {
                // The following is undefined due to the element not existing yet at runtime
                // const never_mind: HTMLButtonElement = document.getElementsByClassName('ms-account-profile__attributes-cancel-button msc-btn')[0] as HTMLButtonElement;

                // Protip: This resets the birthday value on click, so be mindful of that
                bd_input.setAttribute('type', 'date');

                // never_mind.addEventListener("click", () => {
                //     bd_input.setAttribute('type', 'text');
                //     bd_input.value = prev_date;
                // });
            }
        });
    }, []);

    return (
        <Module {...AccountProfile}>
            {heading}
            <div className={'row row-fullwidth'}>
                <div className={'container'}>
                    <div className={'row'}>
                        <div className={'col-lg-4 ms-account-profile__main-info'}>
                            <div>
                                {accountProfileWrapper && (
                                    <Node {...accountProfileWrapper}>
                                        <AccountProfileSection {...emailSection} />
                                        <AccountProfileSection {...nameSection} />
                                        {customerAttributesWrapper && (
                                            <Node {...customerAttributesWrapper}>{customerAttributesSection}</Node>
                                        )}
                                    </Node>
                                )}
                            </div>
                        </div>
                        <div className={'col-lg-4 ms-account-profile__main-info extra'}>
                            {customerAttributesWrapper && <Node {...customerAttributesWrapper}>{customerAttributesSection}</Node>}
                        </div>
                        <div className={'col-lg-4 ms-account-profile__preferences'}>
                            <AccountPreferencesSection {...preferenceSection} />
                        </div>
                    </div>
                </div>
            </div>
        </Module>
    );
};

export default AccountProfileView;
